import { Component, CUSTOM_ELEMENTS_SCHEMA, OnInit, TemplateRef, ViewChild, inject } from '@angular/core';
import { LookupType } from './lookup-type';
import { DatatableColumn } from '../../datatable/datatable-column';
import { AppUrls } from '../../utility/appurls';
import { DatatableSort } from '../../datatable/datatable-sort';
import { GenericLookupService } from './generic-lookup.service';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { GenericLookup, GenericLookupResult } from './generic-lookup';
import { DatatableComponent } from '../../datatable/datatable/datatable.component';
import { EntityTypeCode, EntityTypeName } from '../../utility/system-type-constants';
import { TransactionType } from '../../utility/transaction-constants';
import { GenericLookupForm } from '../lookup-forms/generic-lookup-form';
import { PrefixPipe } from '../../utility/prefix.pipe';
import { SsnMaskPipe } from '../../utility/ssn-mask.pipe';
import { Permissions } from 'projects/bis-shared/src/lib/utility/permissions';
import { FeinDisplayMaskPipe } from '../../utility/fein-display-mask.pipe';
import { EnvironmentService } from '../../environment/environment.service';
import { IdentityService } from '../../auth/identity.service';
import { EntityFormatterPipe } from '../../utility/entity-formatter.pipe';
import { RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { HealthcareProviderLookupFormComponent } from '../lookup-forms/healthcare-provider-lookup/healthcare-provider-lookup-form.component';
import { HealthcareProviderLookupSupervisoryRelationshipFormComponent } from '../lookup-forms/healthcare-provider-supervisory-relationship-lookup/healthcare-provider-lookup-supervisory-relationship-form.component';
import { MedicalSupplierLookupFormComponent } from '../lookup-forms/medical-supplier-lookup/medical-supplier-lookup.component';
import { EmployerLookupFormComponent } from '../lookup-forms/employer-lookup/employer-lookup-form.component';
import { ClaimantLookupFormComponent } from '../lookup-forms/claimant-lookup/claimant-lookup-form.component';
import { InsurerLookupFormComponent } from '../lookup-forms/insurer-lookup-form/insurer-lookup-form.component';
import { InsurerExternalLookupFormComponent } from '../lookup-forms/insurer-external-lookup-form/insurer-external-lookup-form.component';
import { ThirdPartyAdministratorLookupFormComponent } from '../lookup-forms/third-party-administrator-lookup-form/third-party-administrator-lookup-form.component';
import { ThirdPartyAdministratorExternalLookupFormComponent } from '../lookup-forms/third-party-administrator-external-lookup-form/third-party-administrator-external-lookup-form.component';
import { ExternalUserLookupFormComponent } from '../lookup-forms/external-user-lookup-form/external-user-lookup-form.component';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-generic-lookup',
  templateUrl: './generic-lookup.component.html',
  styleUrls: ['./generic-lookup.component.scss'],
  imports: [EntityFormatterPipe, PrefixPipe, RouterModule, MatDialogModule, CommonModule,
    HealthcareProviderLookupFormComponent, HealthcareProviderLookupSupervisoryRelationshipFormComponent,
    MedicalSupplierLookupFormComponent, EmployerLookupFormComponent, ClaimantLookupFormComponent,
    InsurerLookupFormComponent, InsurerExternalLookupFormComponent, ThirdPartyAdministratorLookupFormComponent, 
    ThirdPartyAdministratorExternalLookupFormComponent,ExternalUserLookupFormComponent, DatatableComponent, 
    MatButtonModule, MatIconModule, 
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class GenericLookupComponent implements OnInit {
  private genericLookupService = inject(GenericLookupService);
  private data = inject<GenericLookup>(MAT_DIALOG_DATA);
  private dialogRef = inject<MatDialogRef<GenericLookupComponent>>(MatDialogRef);
  private envService = inject(EnvironmentService);
  private identityService = inject(IdentityService);

  lookupData = new GenericLookup();
  selectedRow: GenericLookupResult;
  query = {};
  initialQueryObject = {};
  bisPermissions = Permissions;

  // Utility properties
  AppUrls = AppUrls;
  EntityType = EntityTypeCode;
  LookupType = LookupType;
  TransactionType = TransactionType;

  // Display properties
  createLink: string;
  searchLink = AppUrls.search();
  canCreate = false;
  hasSearched = false;
  hasSelected = false;
  dataUrl: string;
  createPermission: boolean = false;
  isExternal: boolean;
  lookupTitleType: string;
  isOBLR: boolean = false;

  // Datatable properties
  columns: DatatableColumn[];
  defaultSort: DatatableSort[];
  @ViewChild(DatatableComponent) table: DatatableComponent;

  @ViewChild('nameTemplate', { static: true }) nameTemplate: TemplateRef<any>;

  // Healthcare Provider Templates
  @ViewChild('healthcareIdTemplate', { static: true }) healthcareIdTemplate: TemplateRef<any>;

  // Medical Supplier Templates
  @ViewChild('medicalSupplierIdTemplate', { static: true })
    medicalSupplierIdTemplate: TemplateRef<any>;

  // Claimant Templates
  @ViewChild('claimantIdTemplate', { static: true }) claimantIdTemplate: TemplateRef<any>;
  @ViewChild('ssnTemplate', { static: true }) ssnTemplate: TemplateRef<SsnMaskPipe>;
  @ViewChild('addressTemplate', { static: true }) addressTemplate: TemplateRef<any>;

  // Employer Templates
  @ViewChild('employerIdTemplate', { static: true }) employerIdTemplate: TemplateRef<any>;

  // Insurer Templates
  @ViewChild('insurerIdTemplate', { static: true }) insurerIdTemplate: TemplateRef<any>;
  @ViewChild('insuranceNumberTemplate', { static: true })
    insuranceNumberTemplate: TemplateRef<PrefixPipe>;

  // External Insurer Template
  @ViewChild('insurerExternalIdTemplate', { static: true })
    insurerExternalIdTemplate: TemplateRef<any>;
  @ViewChild('insurerWNumberIdTemplate', { static: true })
    insurerWNumberIdTemplate: TemplateRef<any>;
  @ViewChild('insurerBNumberIdTemplate', { static: true })
    insurerBNumberIdTemplate: TemplateRef<any>;

  // Third Party Administrator Template
  @ViewChild('thirdPartyAdministratorIdTemplate', { static: true })
    thirdPartyAdministratorIdTemplate: TemplateRef<any>;

  // External Third Party Administrator Template
  @ViewChild('thirdPartyAdministratorExternalIdTemplate', { static: true })
    thirdPartyAdministratorExternalIdTemplate: TemplateRef<any>;

  // External Third Party Administrator Template
  @ViewChild('systemUserIdTemplate', { static: true }) systemUserIdTemplate: TemplateRef<any>;

  // Common Templates
  @ViewChild('feinDisplayMaskTemplate', { static: true })
    feinDisplayMaskTemplate: TemplateRef<FeinDisplayMaskPipe>;
  @ViewChild('feinTemplate', { static: true }) feinTemplate: TemplateRef<any>;
  @ViewChild('childForm') childForm: GenericLookupForm;
  @ViewChild('datatable') datatableComponent: DatatableComponent;

  
 

  constructor() {
    const data = this.data;

    this.lookupData = data;
  }

  ngOnInit() {
    this.initialQueryObject = this.lookupData.queryObject || {};
    this.lookupTitleType = this.lookupData.lookupType;

    this.isOBLR = this.envService.getEnvironment().featureFlags.onBoardLimitedRelease;

    this.buildCreateButton();
    this.buildDatatableByLookupType();
    this.buildDataUrl();
  }

  buildDataUrl() {
    this.dataUrl = this.genericLookupService.getDataUrl(this.lookupData.lookupType);
  }

  // Add default sorts and columns by type as new lookups are created
  buildDatatableByLookupType() {
    switch (this.lookupData.lookupType) {
      case LookupType.HealthcareProvider:
        this.columns = this.genericLookupService.buildHealthcareProviderColumns(
          this.healthcareIdTemplate,
        );
        this.defaultSort = this.genericLookupService.buildHealthcareProviderSorts();
        break;
      case LookupType.HealthcareProviderSupervisory:
        this.columns = this.genericLookupService.buildHealthcareProviderColumns(
          this.healthcareIdTemplate,
        );
        this.defaultSort = this.genericLookupService.buildHealthcareProviderSorts();
        break;
      case LookupType.MedicalSupplier:
        this.columns = this.genericLookupService.buildMedicalSupplierColumns(
          this.medicalSupplierIdTemplate,
        );
        this.defaultSort = this.genericLookupService.buildMedicalSupplierSorts();
        break;
      case LookupType.Claimant:
        this.columns = this.genericLookupService.buildClaimantColumns(
          this.claimantIdTemplate,
          this.ssnTemplate,
        );
        this.defaultSort = this.genericLookupService.buildClaimantSorts();
        break;
      case LookupType.Employer:
        this.columns = this.genericLookupService.buildEmployerColumns(
          this.employerIdTemplate,
          this.addressTemplate,
        );
        this.defaultSort = this.genericLookupService.buildEmployerSorts();
        break;
      case LookupType.Insurer:
        this.columns = this.genericLookupService.buildInsurerColumns(
          this.insurerIdTemplate,
          this.insuranceNumberTemplate,
        );
        this.defaultSort = this.genericLookupService.buildInsurerSorts();
        break;
      case LookupType.InsurerExternal:
        this.isExternal = true;
        this.columns = this.genericLookupService.buildInsurerExternalColumns(
          this.insurerWNumberIdTemplate,
          this.insurerBNumberIdTemplate,
          this.feinDisplayMaskTemplate,
        );
        this.defaultSort = this.genericLookupService.buildInsurerSorts();
        break;
      case LookupType.ThirdPartyAdministrator:
        this.columns = this.genericLookupService.buildThirdPartyAdministratorColumns(
          this.thirdPartyAdministratorIdTemplate,
        );
        this.defaultSort = this.genericLookupService.buildThirdPartyAdministratorSorts();
        break;
      case LookupType.ThirdPartyAdministratorExternal:
        this.isExternal = true;
        this.columns = this.genericLookupService.buildThirdPartyAdministratorExternalColumns(
          this.thirdPartyAdministratorExternalIdTemplate,
          this.feinDisplayMaskTemplate,
        );
        this.defaultSort = this.genericLookupService.buildThirdPartyAdministratorSorts();
        break;
      case LookupType.ExternalSystemUser:
        this.isExternal = true;
        this.columns = this.genericLookupService.buildExternalSystemUserColumns(this.nameTemplate);
        this.defaultSort = this.genericLookupService.buildExternalSystemUserSorts();
        break;
      case LookupType.SystemUser:
        this.isExternal = false;
        this.columns = this.genericLookupService.buildSystemUserColumns(this.systemUserIdTemplate);
        this.defaultSort = this.genericLookupService.buildSystemUserSorts();
        break;
      default:
        this.columns = null;
        this.defaultSort = null;
        throw Error('No recognized Lookup Type provided for column/sort creation.');
    }
  }

  // Add new create links and permissions as new lookups are added
  buildCreateButton() {
    switch (this.lookupData.lookupType) {
      case LookupType.HealthcareProvider:
        this.createPermission =
          this.identityService.hasPermissions(
            this.bisPermissions.NonSEDHealthcareProviderDetailsCreate,
          ) && !this.isOBLR;
        this.createLink = AppUrls.entities.healthcareProvider.create();
        break;
      case LookupType.MedicalSupplier:
        this.createPermission =
          this.identityService.hasPermissions(this.bisPermissions.MedicalSupplierDetailsCreate) &&
          !this.isOBLR;
        this.createLink = AppUrls.entities.medicalSupplier.create();
        break;
      case LookupType.Claimant:
        this.createPermission =
          this.identityService.hasPermissions(this.bisPermissions.ClaimantDetailsCreate) &&
          !this.isOBLR;
        this.createLink = AppUrls.entities.claimant.create();
        break;
      case LookupType.Insurer:
        this.createLink = AppUrls.entities.insurer.create();
        break;
      case LookupType.InsurerExternal:
        this.createLink = AppUrls.entities.insurer.create();
        this.lookupTitleType = 'Insurer';
        break;
      case LookupType.ThirdPartyAdministrator:
        this.createPermission =
          this.identityService.hasPermissions(
            this.bisPermissions.ThirdPartyAdministratorDetailsCreate,
          ) && !this.isOBLR;
        this.createLink = AppUrls.entities.thirdPartyAdministrator.create();
        break;
      case LookupType.ThirdPartyAdministratorExternal:
        this.createLink = AppUrls.entities.thirdPartyAdministrator.create();
        this.lookupTitleType = 'TPA';
        break;
      case LookupType.SystemUser:
        this.createPermission = this.identityService.hasPermissions(
          this.bisPermissions.OnboardUserAdministrationAddEditUser,
        );
        break;
      case LookupType.Employer:
      case LookupType.ExternalSystemUser:
      case LookupType.HealthcareProviderSupervisory:
        break;
      default:
        throw Error('No recognized Lookup Type provided for Create button.');
    }
  }

  selectRow(selectedRow: any) {
    this.hasSelected = true;
    this.selectedRow = this.buildLookupResult(selectedRow.selected[0]);
  }

  buildLookupResult(row: any): GenericLookupResult {
    switch (this.lookupData.lookupType) {
      case LookupType.HealthcareProvider:
        return {
          ...row,
          name: `${row.entityId}: ${row.name}`,
          entityName: row.sedName,
          id: row.healthcareProviderId,
        };
      case LookupType.HealthcareProviderSupervisory:
        return {
          ...row,
          name: `${row.entityId}: ${row.name}`,
          entityName: row.sedName,
          id: row.healthcareProviderId,
        };
      case LookupType.MedicalSupplier:
        return {
          ...row,
          name: `${row.entityId}: ${row.medicalSupplierName}`,
          entityName: row.medicalSupplierName,
          id: row.medicalSupplierId,
        };
      case LookupType.Claimant:
        return {
          ...row,
          name: `${row.entityId}: ${row.lastName}, ${row.firstName}`,
          entityName: `${row.lastName}, ${row.firstName}`,
          id: row.claimantId,
        };
      case LookupType.Employer:
        return {
          ...row,
          name: `${row.employerNumber}: ${row.employerName}`,
          entityName: row.employerName,
          id: row.employerId,
          bisId: row.bisId,
        };
      case LookupType.Insurer:
        return {
          ...row,
          name: `${row.entityId}: ${row.insurerName}`,
          entityName: row.insurerName,
          id: row.insurerId,
        };
      case LookupType.InsurerExternal:
        return {
          ...row,
          name: `${row.entityId}: ${row.insurerName}`,
          entityName: row.insurerName,
          id: row.insurerId,
        };
      case LookupType.ThirdPartyAdministrator:
        return {
          ...row,
          name: `${row.entityId}: ${row.thirdPartyAdministratorName}`,
          entityName: row.thirdPartyAdministratorName,
          id: row.thirdPartyAdministratorId,
        };
      case LookupType.ThirdPartyAdministratorExternal:
        return {
          ...row,
          name: `${row.entityId}: ${row.thirdPartyAdministratorName}`,
          entityName: row.thirdPartyAdministratorName,
          id: row.thirdPartyAdministratorId,
        };
      case LookupType.ExternalSystemUser:
        return {
          ...row,
          name: this.getFormattedUserName(row),
          entityName: `${row.lastName}`,
          id: row.id,
        };
      case LookupType.SystemUser:
        return {
          ...row,
          name: `${row.userName}`,
          entityName: row.userName,
          id: row.id,
        };
      default:
        throw Error('No recognized Lookup Type provided for selected row.');
    }
  }

  getFormattedUserName(row: any): string {
    let result = '';

    if (row.lastName) {
      result += row.lastName;
      if (row.firstName || row.middleName) {
        result += ', ';
      }
      if (row.firstName) {
        result += row.firstName + ' ';
      }
      if (row.middleName) {
        result += row.middleName;
      }
    } else {
      return row.userName;
    }

    return result;
  }

  // Opens link in a new tab for given url
  openLinkInNewTab(url: string) {
    switch (this.lookupData.lookupType) {
      case LookupType.MedicalSupplier:
      case LookupType.ThirdPartyAdministrator:
        window.open(`${url}`);
        break;
      default:
        alert(`Create New ${this.lookupData.lookupType} will be implemented in a future cycle.`);
    }
  }

  search() {
    if (!this.childForm.isValid()) {
      return;
    }
    const val = this.childForm.getValue();
    this.query = { ...val, ...this.initialQueryObject };
    this.hasSearched = true;
    this.hasSelected = false;
    this.selectedRow = undefined;
    // Push down stack to make sure criteria has been properly set
    setTimeout(() => this.datatableComponent.reloadTable(), 0);
  }

  goToFullSearchLink(url: string, queryParams: string) {
    if (queryParams === LookupType.ThirdPartyAdministrator) {
      queryParams = EntityTypeName.ThirdPartyAdministrator;
    }
    window.open(`${url}?searchType=${queryParams}`);
  }

  clear() {
    if (this.childForm) {
      this.childForm.reset();
    }

    this.hasSearched = false;
    this.hasSelected = false;
    this.selectedRow = undefined;
  }

  closeDialog() {
    this.dialogRef.close();
  }
}
